import { ExportResultCode } from '@opentelemetry/core';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { fetchAuthSession } from 'aws-amplify/auth';

import type { ExportResult } from '@opentelemetry/core';
import type { ReadableSpan, SpanExporter } from '@opentelemetry/sdk-trace-base';

/**
 * Cognito(Amplify) の idToken を送信のたびに取得し、Authorization: Bearer ヘッダを
 * 付与してから内部の OTLPTraceExporter へ委譲する SpanExporter ラッパー。
 *
 * ## 設計上の制約
 * OTel の初期化は main.ts の bootstrapApplication より前に行うため、本クラスは
 * Angular DI（inject / AuthService）を使えない。そのため AuthService を経由せず、
 * aws-amplify/auth の fetchAuthSession を直接呼ぶ。Amplify.configure は bootstrap 中
 * （provideAppInitializer）に実行されるが、span のバッチ送信（export）が走るのは
 * 認証後のため、実行時には configure 済みである。
 *
 * ## 未認証時の挙動
 * ログイン画面の document-load span 等、認証前に生成された trace はトークンを取得できない。
 * この場合は **エクスポートを静かにスキップして span を破棄** し、resultCallback には
 * ExportResultCode.SUCCESS を返す。FAILED を返すと BatchSpanProcessor がリトライを重ね、
 * 認証必須の受け口へ 401 リクエストを繰り返し送ってしまうため、SUCCESS で「処理済み・破棄」
 * として扱う（アーキテクチャ決定: 認証必須、ログイン前 trace は捨てる）。
 */
class AuthenticatedOtlpExporter implements SpanExporter {
  /** 内部の OTLPTraceExporter。実際の HTTP 送信を担う。 */
  private readonly inner: OTLPTraceExporter;
  /**
   * 直近の export で取得した idToken。inner の headers factory が送信直前に参照する。
   * export() がトークン取得に成功したときのみ更新され、その直後に inner.export() を呼ぶ。
   */
  private currentToken: string | undefined;

  public constructor(url: string) {
    this.inner = new OTLPTraceExporter({
      url,
      // headers は () => Promise<Record<string, string>> を受け付け、送信直前に毎回呼ばれる。
      // export() で currentToken を更新してから inner.export() へ委譲するため、常に最新トークンが載る。
      headers: (): Promise<Record<string, string>> => {
        const headers: Record<string, string> = this.currentToken
          ? { Authorization: `Bearer ${this.currentToken}` }
          : {};
        return Promise.resolve(headers);
      },
    });
  }

  public export(
    spans: ReadableSpan[],
    resultCallback: (result: ExportResult) => void,
  ): void {
    // 送信のたびに最新の idToken を取得する。取得できなければ委譲せず破棄する。
    fetchAuthSession()
      .then((session) => {
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) {
          // 未認証（ログイン前 trace 等）: 静かに破棄する。
          resultCallback({ code: ExportResultCode.SUCCESS });
          return;
        }
        this.currentToken = idToken;
        this.inner.export(spans, resultCallback);
      })
      .catch(() => {
        // fetchAuthSession 失敗（未認証等）: 401 送信を繰り返さないよう破棄扱いで SUCCESS を返す。
        resultCallback({ code: ExportResultCode.SUCCESS });
      });
  }

  public shutdown(): Promise<void> {
    return this.inner.shutdown();
  }

  public forceFlush(): Promise<void> {
    return this.inner.forceFlush();
  }
}

/**
 * 認証付き OTLPTraceExporter を生成する。
 * @param url 送信先 URL（例: `${environment.apiUrl}/telemetry/traces`）。呼び出し側（otel-init）で組み立てる。
 */
export function createAuthenticatedOtlpExporter(url: string): SpanExporter {
  return new AuthenticatedOtlpExporter(url);
}
